import {
  IResponseActivity,
  IResponseAllActivity,
  IResponseSendAsset,
  IResponseVerifyAddress,
} from '@/core/domain/entities/activities';
import {
  IRequestEstimateFee,
  IRequestSendAllAsset,
  IRequestSendAsset,
  IRequestTokenTransactions,
  IRequestTransactions,
} from '@/core/domain/entities/request';
import { IResponseBalance } from '@/core/domain/entities/user';
import {
  mapAllTransactions,
  mapSendAsset,
  mapTransactions,
  mapVerifyAddress,
} from '@/core/domain/mapper/mapActivities';
import { checklocal } from '@/utils';
import axios from 'axios';

/**
 * 트론 거래내역 조회
 * @param {IRequestTransactions} reqData
 * @returns {Promise<string | IResponseActivity[]>}
 */
export async function fetchTransactions(reqData: IRequestTransactions): Promise<string | IResponseActivity[]> {
  try {
    const response = await axios.post(`${checklocal()}/wallet/trx/transactions`, reqData);

    return mapTransactions(response.data);
  } catch (err) {
    console.error('Transaction error:', err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }
    return '0000';
  }
}

/**
 * 모든 거래내역 조회
 * @param {IRequestTransactions} reqData
 * @returns {Promise<string | IResponseActivity[]>}
 */
export async function fetchAllTransactions(
  reqData: IRequestTransactions,
): Promise<{ status: string; message: string; response: IResponseAllActivity[] }> {
  try {
    const response = await axios.post(`${checklocal()}/wallet/transactions`, reqData);

    if (response.data.status === 'error') {
      return { status: 'error', message: response.data.message, response: response.data };
    } else {
      return { status: 'success', message: '거래목록 조회 성공', response: mapAllTransactions(response.data.result) };
    }
  } catch (err) {
    console.error('Transaction error:', err);
    if (axios.isAxiosError(err)) {
      return { status: 'error', message: err.response?.data.errorCode.message, response: [] };
    }
    return { status: 'error', message: '거래목록 조회 실패', response: [] };
  }
}

/**
 * 토큰 거래내역 조회
 * @param {IRequestTokenTransactions} reqData
 * @returns {Promise<string | IResponseActivity[]>}
 */
export async function fetchTokenTransactions(
  reqData: IRequestTokenTransactions,
): Promise<string | IResponseActivity[]> {
  try {
    const response = await axios.post(`${checklocal()}/wallet/token/transactions`, reqData);
    // mock data
    // const response = MOCK_TOKEN_TRANSACTIONS;

    return mapTransactions(response.data.result);
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

/**
 * 토큰 환율 조회
 * @returns {Promise<string | number>}
 */
export async function checkTokenPrice(): Promise<string | number> {
  try {
    const response = await axios.get(`${checklocal()}/wallet/token/price`);

    return response.data.result;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

/**
 * 트론 환율 조회
 * @returns {Promise<string | number>}
 */
export async function checkTrxPrice(): Promise<string | number> {
  try {
    const response = await axios.get(`${checklocal()}/wallet/trx/price`);

    return response.data.result;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

/**
 * 토큰 전송
 *
 * @param {IRequestSendAsset} reqData
 * @returns {Promise<string | IResponseSendAsset>}
 */
export async function sendAsset(reqData: IRequestSendAsset): Promise<string | IResponseSendAsset> {
  try {
    const response = await axios.post(`${checklocal()}/wallet/token/transfer`, reqData);

    return mapSendAsset(response.data.result);
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

/**
 * 트론 전송
 *
 * @export
 * @param {IRequestSendAsset} reqData
 * @return {*}  {(Promise<string | IResponseSendAsset>)}
 */
export async function sendTrx(reqData: IRequestSendAsset): Promise<string | IResponseSendAsset> {
  try {
    const response = await axios.post(`${checklocal()}/wallet/trx/transfer`, reqData);

    return mapSendAsset(response.data.result);
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

/**
 * 트론 전송
 *
 * @export
 * @param {IRequestSendAsset} reqData
 * @return {*}  {(Promise<string | IResponseSendAsset>)}
 */
export async function sendCoinAndToken(reqData: IRequestSendAllAsset): Promise<string | IResponseSendAsset> {
  try {
    const response = await axios.post(`${checklocal()}/wallet/sol/transfer`, reqData);

    return mapSendAsset(response.data);
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

/**
 * 주소 검증
 *
 * @export
 * @param {string} address
 * @return {*}  {(Promise<string | IResponseVerifyAddress>)}
 */
export async function verifyAddress(address: string): Promise<string | IResponseVerifyAddress> {
  try {
    const body = {
      address: address,
      blockChain: 'sol',
    };

    const response = await axios.post(`${checklocal()}/wallet/verifyaddress`, body);

    return mapVerifyAddress(response.data.result);
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

/**
 * 잔액 조회
 * @param {string} address
 * @returns {Promise<string | IResponseBalance>}
 */
export async function fetchBalance(address: string): Promise<string | IResponseBalance> {
  try {
    const response = await axios.post(`${checklocal()}/wallet/balance`, { address });

    return response.data.result;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

/**
 * 수수료 조회
 * @param {IRequestFee} reqData
 * @returns {Promise<string | number>}
 */
export async function fetchFee(reqData: IRequestEstimateFee): Promise<string | number> {
  try {
    const response = await axios.post(`${checklocal()}/wallet/estimate-fee`, reqData);
    // result 객체에서 estimatedFee만 추출
    return response.data.result?.estimatedFee ?? response.data.result;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data?.errorCode?.code || '0000';
    }
    return '0000';
  }
}

export async function getMemo(
  id: string,
  address: string,
): Promise<{ status: string; result: string; errorMessage: string }> {
  try {
    const response = await axios.post(`${checklocal()}/wallet/getmemo`, { id, address });
    return { status: response.data.status, result: response.data.result, errorMessage: response.data.message };
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return { status: 'error', result: '', errorMessage: err.response?.data?.errorCode?.message || '' };
    }
    return { status: 'error', result: '', errorMessage: '메모 조회 실패' };
  }
}


export async function getIsLockedUser(
  phone: string,
): Promise<{ status: string; result: string; errorMessage: string }> {
  try {
    const response = await axios.post(`${checklocal()}/admin/checkLock`, { phone });
    return { status: response.data.status, result: response.data.result, errorMessage: response.data.message };
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return { status: 'error', result: '', errorMessage: err.response?.data?.errorCode?.message || '' };
    }
    return { status: 'error', result: '', errorMessage: '메모 조회 실패' };
  }
}
